import type { ReactNode } from 'react';

interface LedgerToolbarProps {
  types: { id: string; label: string; count: number }[];
  type: string;
  onType: (id: string) => void;
  wallets: readonly string[];
  wallet: string;
  onWallet: (value: string) => void;
  reconOptions: readonly string[];
  recon: string;
  onRecon: (value: string) => void;
  feeOptions: readonly string[];
  fees: string;
  onFees: (value: string) => void;
  query: string;
  onQuery: (value: string) => void;
  from: string;
  onFrom: (value: string) => void;
  to: string;
  onTo: (value: string) => void;
  activeCount: number;
  onReset: () => void;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-500">
        {label}
      </span>
      {children}
    </label>
  );
}

const controlClass =
  'h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm font-label text-foreground-800 focus:border-primary-400 focus:outline-none';

export default function LedgerToolbar({
  types,
  type,
  onType,
  wallets,
  wallet,
  onWallet,
  reconOptions,
  recon,
  onRecon,
  feeOptions,
  fees,
  onFees,
  query,
  onQuery,
  from,
  onFrom,
  to,
  onTo,
  activeCount,
  onReset,
}: LedgerToolbarProps) {
  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Field label="Wallet">
          <div className="relative">
            <select
              value={wallet}
              onChange={(event) => onWallet(event.target.value)}
              className={`${controlClass} cursor-pointer appearance-none pr-8`}
            >
              {wallets.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>
        </Field>

        <Field label="Search">
          <div className="flex h-9 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
            <i className="ri-search-line text-sm text-foreground-400" />
            <input
              type="text"
              value={query}
              onChange={(event) => onQuery(event.target.value)}
              placeholder="Reference, payee or description..."
              className="h-full w-full min-w-0 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
            />
          </div>
        </Field>

        <Field label="From">
          <input
            type="date"
            value={from}
            onChange={(event) => onFrom(event.target.value)}
            className={`${controlClass} cursor-pointer`}
          />
        </Field>

        <Field label="To">
          <input
            type="date"
            value={to}
            onChange={(event) => onTo(event.target.value)}
            className={`${controlClass} cursor-pointer`}
          />
        </Field>

        <Field label="Fees">
          <div className="relative">
            <select
              value={fees}
              onChange={(event) => onFees(event.target.value)}
              className={`${controlClass} cursor-pointer appearance-none pr-8`}
            >
              {feeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>
        </Field>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-background-100 pt-4">
        <Field label="Type">
          <div className="relative">
            <select
              value={type}
              onChange={(event) => onType(event.target.value)}
              className={`${controlClass} w-full cursor-pointer appearance-none pr-8 sm:w-52`}
            >
              {types.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                  {option.count ? ` (${option.count})` : ''}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>
        </Field>

        <Field label="Reconciled">
          <div className="relative">
            <select
              value={recon}
              onChange={(event) => onRecon(event.target.value)}
              className={`${controlClass} w-full cursor-pointer appearance-none pr-8 sm:w-44`}
            >
              {reconOptions.map((option) => (
                <option key={option} value={option}>
                  {option === 'All' ? 'All reconciliation' : option}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>
        </Field>

        <div className="flex items-center gap-2 pb-0.5">
          <span className="brand-cta inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-sm font-label font-medium text-background-50 dark:text-foreground-950">
            <i className="ri-filter-3-line" />
            {activeCount > 0 ? `${activeCount} filter${activeCount === 1 ? '' : 's'}` : 'No filters'}
          </span>
          <button
            type="button"
            onClick={onReset}
            disabled={activeCount === 0}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Clear filters"
            title="Clear filters"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      </div>
    </section>
  );
}